import React, { useEffect, useRef, useState } from "react";
import CurrencyToggle from "../currencyToggle";
import MoreDropdown from "./MoreDropdown";
import {
    Eye,
    EyeSlash,
    Money,
    Global,
    BitcoinConvert,
    ArrowCircleRight,
} from "iconsax-reactjs";
import { ConvertIcon, FundWalletBold, MoreBold, SendMoneyBold } from "../../asset/icons";
import currencyPairCheck from "utilities/currencyPairCheck";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AccountBalancesDrawer from "./AccountBalancesDrawer";

export default function WalletBalanceCard(props) {
    const {
        businessId,
        defaults,
        isBusinessApproved,
        accountLoading,
        account,
        onSelectTransfer,
        onOpenFundModal,
        onOpenFundingMethodModal,
        onOpenAccountDetails,
        onOpenGenerateStatement,
    } = props;
    const navigate = useNavigate();
    const [showBalance, setShowBalance] = useState(true);
    const [showMoreDropdown, setShowMoreDropdown] = useState(false);
    const [showSendDropdown, setShowSendDropdown] = useState(false);
    const [showBalancesDrawer, setShowBalancesDrawer] = useState(false);
    const wallets = useSelector((s) => s?.wallets || []);

    const dropdownWrapperRef = useRef(null);
    const sendDropdownRef = useRef(null);

    const isCryptoWallet = defaults === "USDT" || defaults === "USDC";
    const canConvert = wallets.length > 1;
    const { symbol, isCrypto } = currencyPairCheck(defaults, { returnType: "full" });

    const providers = account?._raw?.wallet?.providers;
    const hasMultipleProviders = Array.isArray(providers) && providers.length > 1;
    const showBalancesLink = isCryptoWallet || (hasMultipleProviders && defaults !== "KES");

    const accountReady = !accountLoading && !!account;

    const balanceAmount = account?.balance ?? 0;
    const accountNumber = account?.accountNumber || "";
    const bankName = account?.bankName || account?.bank || "";

    const formattedBalance = balanceAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    const [whole, frac = "00"] = formattedBalance.split(".");

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownWrapperRef.current && !dropdownWrapperRef.current.contains(e.target)) {
                setShowMoreDropdown(false);
            }
            if (sendDropdownRef.current && !sendDropdownRef.current.contains(e.target)) {
                setShowSendDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const actions = [
        {
            label: "Fund",
            icon: <FundWalletBold className="w-6 h-6 sm:w-8 sm:h-8" />,
            onClick: () => {
                setShowMoreDropdown(false);
                setShowSendDropdown(false);
                isCryptoWallet ? onOpenFundingMethodModal?.() : onOpenFundModal?.();
            },
        },

        ...(!isCryptoWallet
            ? [
                  {
                      label: "Convert",
                      icon: <ConvertIcon />,
                      disabled: !canConvert,
                      onClick: () => {
                          if (!canConvert) return;
                          if (!isBusinessApproved) return;
                          navigate("/convert-currency");
                      },
                  },
              ]
            : []),
        {
            label: "Send",
            icon: <SendMoneyBold />,
            onClick: () => {
                if (!isBusinessApproved) return;
                setShowMoreDropdown(false);
                setShowSendDropdown((prev) => !prev);
            },
        },
        { label: "More", icon: <MoreBold />, isMore: true },
    ];

    return (
        <>
            <div className="col-span-1 p-1 bg-white rounded-xl">
            <div className="bg-[#F2F3FC] p-3 rounded-xl">
                {/* Top row */}
                <div className="flex items-center justify-between">
                    <CurrencyToggle
                        className="bg-[#F2F3FC]"
                        dropDownClassName="shadow-dropdown2 !left-0 !top-[109%]"
                        isDisabled={!isBusinessApproved}
                        fetchWallet
                        businessId={businessId}
                    />

                    {!accountReady ? (
                        <div className="h-4 w-36 bg-gray-200 animate-pulse rounded-md" />
                    ) : (
                        showBalancesLink && (
                            <div
                                className="flex items-center cursor-pointer gap-2 text-sm text-gray-600"
                                onClick={() => setShowBalancesDrawer(true)}
                            >
                                View balances{" "}
                                <ArrowCircleRight size="20" color="#C4C6DE" variant="Bold" />
                            </div>
                        )
                    )}

                    {!accountReady ? (
                        <div className="h-4 w-36 bg-gray-200 animate-pulse rounded-md" />
                    ) : (
                        !showBalancesLink && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                {bankName || "Tradevu wallet"} •{" "}
                                <span className="text-main-dark">{accountNumber}</span>
                            </div>
                        )
                    )}
                </div>

                {/* Balance */}
                <div className="mt-2 flex items-center">
                    {!accountReady ? (
                        <div className="h-4 w-36 bg-gray-200 animate-pulse rounded-md" />
                    ) : showBalance ? (
                        isCrypto ? (
                            <span className="text-[40px] font-[450] text-[#383B56]">
                                {formattedBalance}
                                <span className="ml-1 text-[24px] font-[350] text-[#383B56]">
                                    {symbol}
                                </span>
                            </span>
                        ) : (
                            <span className="text-[40px] font-[450] text-[#383B56]">
                                <span className="mr-1 text-[32px] font-[350]">{symbol}</span>
                                {whole}
                                <span className="text-[24px] font-[350] text-[#6B7280]">
                                    .{frac}
                                </span>
                            </span>
                        )
                    ) : (
                        <>••••••••••</>
                    )}

                    <button
                        onClick={() => setShowBalance((s) => !s)}
                        className="ml-3 focus:outline-none"
                    >
                        {showBalance ? (
                            <EyeSlash size="21" color="#27293F" />
                        ) : (
                            <Eye size="21" color="#27293F" />
                        )}
                    </button>
                </div>
            </div>

            <div
                className="relative flex items-center mt-3 justify-evenly"
                ref={dropdownWrapperRef}
            >
                {actions.filter(Boolean).map((action, idx) => {
                    if (!action) return null;
                    if (action.isMore) {
                        return (
                            <div
                                key={idx}
                                className="flex flex-col items-center gap-1 px-2 py-2 sm:px-4"
                            >
                                <button
                                    onClick={() => setShowMoreDropdown((p) => !p)}
                                    className="w-12 h-12 sm:w-16 sm:h-16  border-solid  border-[#F2F3FC] rounded-full bg-[#FBFCFE] flex items-center justify-center"
                                >
                                    <MoreBold />
                                </button>
                                <span className="text-[10px] sm:text-sm text-[#101828]">More</span>

                                {showMoreDropdown && (
                                    <div className="absolute right-0 z-50 mt-2 top-full">
                                        <MoreDropdown
                                            onAccountDetails={() => {
                                                setShowMoreDropdown(false);
                                                onOpenAccountDetails?.();
                                            }}
                                            onGenerateStatement={() => {
                                                setShowMoreDropdown(false);
                                                onOpenGenerateStatement?.();
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    }

                    if (action.label === "Send") {
                        return (
                            <div
                                key={idx}
                                className="relative flex flex-col items-center gap-1 px-2 py-2 sm:px-4"
                                ref={sendDropdownRef}
                            >
                                <button
                                    onClick={action.onClick}
                                    className={`w-12 h-12 sm:w-16 sm:h-16  border-solid  border-[#F2F3FC] rounded-full bg-[#FBFCFE] flex items-center justify-center ${
                                        !isBusinessApproved ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                                >
                                    {React.isValidElement(action.icon)
                                        ? React.cloneElement(action.icon, {
                                              className: "w-6 h-6 sm:w-8 sm:h-8",
                                          })
                                        : null}
                                </button>
                                <span className="text-[10px] sm:text-sm text-[#101828]">Send</span>

                                {showSendDropdown && (
                                    <div className="absolute top-full -left-30 md:left-0 z-50 w-[360px] bg-white shadow-dropdown rounded-xl border border-[#F1F1F1] transition-all duration-300">
                                        {(isCryptoWallet
                                            ? [
                                                  {
                                                      title: `Send ${symbol} to a wallet`,
                                                      subtitle: `Send ${symbol} to any crypto wallet address`,
                                                      icon: (
                                                          <BitcoinConvert
                                                              size="20"
                                                              color="#292D32"
                                                          />
                                                      ),
                                                      type: "wallet",
                                                  },
                                                  {
                                                      title: `Send ${symbol} to fiat`,
                                                      subtitle: `Send ${symbol} to other fiat currencies`,
                                                      icon: <Money size="20" color="#292D32" />,
                                                      type: "fiat",
                                                  },
                                              ]
                                            : [
                                                  {
                                                      title: "Same currency transfer",
                                                      subtitle:
                                                          "Send money within the same currency",
                                                      icon: <Money size="20" color="#292D32" />,
                                                      type: "same",
                                                  },
                                                  {
                                                      title: "Cross-border transfer",
                                                      subtitle:
                                                          "Send money between different currencies",
                                                      icon: <Global size="20" color="#9CA3AF" />,
                                                      type: "cross",
                                                  },
                                              ]
                                        ).map(({ title, subtitle, icon, type, comingSoon }) => (
                                            <div
                                                key={type}
                                                className={`flex items-start border m-2 rounded-lg gap-3 p-2 ${
                                                    comingSoon
                                                        ? "opacity-50 cursor-not-allowed"
                                                        : "hover:bg-[#F9FAFB] cursor-pointer"
                                                }`}
                                                onClick={() => {
                                                    if (comingSoon) return;
                                                    setShowSendDropdown(false);
                                                    onSelectTransfer?.(type);
                                                }}
                                            >
                                                <div className="w-5 h-5 mt-1">{icon}</div>
                                                <div className="flex-1">
                                                    <p className="flex items-center gap-2 font-semibold text-main">
                                                        {title}
                                                        {comingSoon && (
                                                            <span className="text-xs bg-[#F4EBFF] text-[#6941C6] font-medium px-2 py-1 rounded-full">
                                                                Coming soon
                                                            </span>
                                                        )}
                                                    </p>
                                                    <p className="text-[#6B7280] text-sm">
                                                        {subtitle}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }

                    return (
                        <div
                            key={idx}
                            className="flex flex-col items-center gap-1 px-2 py-2 sm:px-4"
                        >
                            <button
                                onClick={action.onClick}
                                disabled={action.disabled}
                                title={
                                    action.label === "Convert" && action.disabled
                                        ? "Open another wallet to convert currencies"
                                        : ""
                                }
                                className={`w-12 h-12 sm:w-16 sm:h-16 border-solid border-[#F2F3FC] rounded-full bg-[#FBFCFE] flex items-center justify-center ${
                                    action.disabled ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                            >
                                {React.isValidElement(action.icon)
                                    ? React.cloneElement(action.icon, {
                                          className: "w-6 h-6 sm:w-8 sm:h-8",
                                      })
                                    : null}
                            </button>
                            <span className="text-[10px] sm:text-sm text-[#101828]">
                                {action.label}
                            </span>
                        </div>
                    );
                })}
            </div>
            </div>

            <AccountBalancesDrawer
                isOpen={showBalancesDrawer}
                onClose={() => setShowBalancesDrawer(false)}
                wallet={account?._raw?.wallet}
                currency={defaults}
            />
        </>
    );
}
